// Sistema de Classificação Legal para Idosos - Baseado em RDC ANVISA 283/2005
// Conforme Estatuto do Idoso (Lei 10.741/2003) e normas brasileiras

export type GrauClassificacao = 'I' | 'I-com-apoio' | 'II' | 'III'

export interface ClassificacaoResultado {
  grau: GrauClassificacao
  pontuacao: number
  descricao: string
  nivelCuidado: 'básico' | 'intermediário' | 'intensivo'
  criteriosAplicados: string[]
  fundamentacaoLegal: string[]
  detalhes: {
    funcional: number
    cognitivo: number
    clinico: number
    sensorial: number
    nutricional: number
  }
}

export interface AnamneseData {
  nome_completo?: string
  data_nascimento?: string
  cpf?: string
  rg?: string
  endereco?: {
    rua?: string
    numero?: string
    complemento?: string
    bairro?: string
    cidade?: string
    estado?: string
    cep?: string
  }
  responsavel?: {
    nome?: string
    parentesco?: string
    whatsapp?: string
    email?: string
    responsavel_financeiro?: boolean
    existe_curatela?: boolean
  }
  comorbidades?: Array<{
    possui: boolean
    condicao: string
    detalhes?: string
  }>
  medicamentos?: Array<{
    name: string
    instructions: string
    recurrence: 'daily' | 'range' | 'once'
    recurrenceStart?: string
    recurrenceEnd?: string
    schedules: string[]
    medicationForm?: 'comprimido' | 'liquido' | 'inalatorio_spray' | 'aplicacao'
    measurementUnit?: 'inteiro' | 'fracionado_1_2' | 'fracionado_1_4' | 'gotas' | 'ml' | 'jatos' | 'ampola'
    quantityPerDose?: number
  }>
  funcionalidade?: Array<{
    atividade: string
    nivel_dependencia: string
    observacoes?: string
  }>
  cognicao?: Record<string, boolean>
  mobilidade?: Record<string, boolean> & {
    observacoes?: string
  }
  nutricao?: {
    peso?: string
    altura?: string
    dieta?: string
    engasgos?: boolean
    sonda?: boolean
    perda_peso?: boolean
  }
  sensorial?: Record<string, boolean>
}

export function calcularClassificacaoLegal(formData: AnamneseData): ClassificacaoResultado {
  // Critérios conforme RDC ANVISA 283/2005 e Estatuto do Idoso
  let pontos = 0
  const criterios: string[] = []
  
  // === AVALIAÇÃO FUNCIONAL (40% do peso) ===
  let pontosFuncional = 0
  
  // Análise das atividades de vida diária (AVDs)
  const atividadesDependentes = formData.funcionalidade?.filter(f => 
    f.nivel_dependencia === 'dependencia_total' || 
    f.nivel_dependencia === 'ajuda_parcial' ||
    f.nivel_dependencia === 'supervisao'
  ).length || 0
  
  // Pontuação por nível de dependência funcional
  if (atividadesDependentes >= 8) {
    pontosFuncional += 40
    criterios.push('Dependência funcional em 8+ AVDs')
  } else if (atividadesDependentes >= 5) {
    pontosFuncional += 25
    criterios.push('Dependência funcional em 5-7 AVDs')
  } else if (atividadesDependentes >= 1) {
    pontosFuncional += 15
    criterios.push('Dependência funcional em 1-4 AVDs')
  }
  
  // Fatores críticos de mobilidade
  if (formData.mobilidade?.acamada) {
    pontosFuncional += 15
    criterios.push('Condição de acamado(a)')
  }
  if (formData.mobilidade?.cadeira_rodas) {
    pontosFuncional += 10
    criterios.push('Uso de cadeira de rodas')
  }
  if (formData.mobilidade?.claudica) {
    pontosFuncional += 5
    criterios.push('Claudicação/limitação locomotora')
  }
  
  // === AVALIAÇÃO COGNITIVA (25% do peso) ===
  let pontosCognitivo = 0
  
  // Fatores cognitivos críticos
  if (formData.cognicao?.demencia) {
    pontosCognitivo += 18
    criterios.push('Diagnóstico de demência')
  }
  if (formData.cognicao?.alzheimer) {
    pontosCognitivo += 20
    criterios.push('Diagnóstico de Alzheimer')
  }
  if (formData.cognicao?.reconhece_familiares === false) {
    pontosCognitivo += 15
    criterios.push('Déficit de reconhecimento familiar')
  }
  if (formData.cognicao?.esquece_eventos) {
    pontosCognitivo += 10
    criterios.push('Esquecimento de eventos recentes')
  }
  if (formData.cognicao?.alucinacoes) {
    pontosCognitivo += 15
    criterios.push('Presença de alucinações')
  }
  if (formData.cognicao?.agressividade || formData.cognicao?.agitacao || formData.cognicao?.fuga) {
    pontosCognitivo += 12
    criterios.push('Alterações de comportamento/agitação')
  }
  
  // === AVALIAÇÃO CLÍNICA (20% do peso) ===
  let pontosClinico = 0
  
  // Comorbidades
  const comorbidadesAtivas = formData.comorbidades?.filter(c => c.possui).length || 0
  if (comorbidadesAtivas >= 5) {
    pontosClinico += 15
    criterios.push('5+ comorbidades ativas')
  } else if (comorbidadesAtivas >= 3) {
    pontosClinico += 10
    criterios.push('3-4 comorbidades ativas')
  } else if (comorbidadesAtivas >= 1) {
    pontosClinico += 5
    criterios.push('1-2 comorbidades ativas')
  }
  
  // Medicamentos
  const numMedicamentos = formData.medicamentos?.length || 0
  if (numMedicamentos >= 8) {
    pontosClinico += 10
    criterios.push('8+ medicamentos contínuos')
  } else if (numMedicamentos >= 5) {
    pontosClinico += 7
    criterios.push('5-7 medicamentos contínuos')
  } else if (numMedicamentos >= 3) {
    pontosClinico += 4
    criterios.push('3-4 medicamentos contínuos')
  }
  
  // === AVALIAÇÃO SENSORIAL (10% do peso) ===
  let pontosSensorial = 0
  
  // Contar apenas déficits reais, ignorando opções "nenhuma" e nulos
  const deficitsSensoriais = Object.entries(formData.sensorial || {})
    .filter(([key, value]) => value === true && !key.startsWith('nenhuma_opcao_'))
    .length

  if (deficitsSensoriais >= 3) {
    pontosSensorial += 8
    criterios.push('3+ déficits sensoriais')
  } else if (deficitsSensoriais >= 2) {
    pontosSensorial += 5
    criterios.push('2 déficits sensoriais')
  } else if (deficitsSensoriais >= 1) {
    pontosSensorial += 3
    criterios.push('1 déficit sensorial')
  }
  
  // === AVALIAÇÃO NUTRICIONAL (5% do peso) ===
  let pontosNutricional = 0
  
  // Cálculo IMC se disponível
  if (formData.nutricao?.peso && formData.nutricao?.altura) {
    const peso = parseFloat(formData.nutricao.peso)
    const altura = parseFloat(formData.nutricao.altura) / 100 // converter cm para m
    const imc = peso / (altura * altura)
    
    if (imc < 18.5) {
      pontosNutricional += 5
      criterios.push('Baixo peso (IMC < 18.5)')
    } else if (imc > 30) {
      pontosNutricional += 3
      criterios.push('Obesidade (IMC > 30)')
    }
  }
  
  // Riscos nutricionais
  if (formData.nutricao?.perda_peso) {
    pontosNutricional += 4
    criterios.push('Perda de peso recente')
  }
  if (formData.nutricao?.sonda) {
    pontosNutricional += 5
    criterios.push('Uso de sonda alimentação')
  }
  if (formData.nutricao?.engasgos) {
    pontosNutricional += 3
    criterios.push('Engasgos frequentes')
  }
  
  // === CÁLCULO FINAL ===
  pontos = pontosFuncional + pontosCognitivo + pontosClinico + pontosSensorial + pontosNutricional
  
  // Normalização para escala 0-100
  const pontuacaoFinal = Math.min(pontos, 100)
  
  // === CLASSIFICAÇÃO FINAL ===
  let grau: GrauClassificacao
  let descricao: string
  let nivelCuidado: 'básico' | 'intermediário' | 'intensivo'

  if (pontuacaoFinal <= 25) {
    const temDependenciaFuncional = atividadesDependentes >= 1;
    if (temDependenciaFuncional) {
      grau = 'I-com-apoio'
      descricao = 'Independente com Apoio - Autonomia com necessidade de apoio pontual'
      nivelCuidado = 'básico'
      criterios.push('Necessidade de apoio pontual em atividades de vida diária')
    } else {
      grau = 'I'
      descricao = 'Independente - Autonomia para atividades básicas e instrumentais'
      nivelCuidado = 'básico'
    }
  } else if (pontuacaoFinal <= 50) {
    grau = 'II'
    descricao = 'Dependência Parcial - Necessita ajuda para algumas atividades'
    nivelCuidado = 'intermediário'
  } else {
    grau = 'III'
    descricao = 'Dependência Total - Necessita ajuda para todas as atividades'
    nivelCuidado = 'intensivo'
  }
  
  // Fundamentação legal
  const fundamentacaoLegal = [
    'RDC ANVISA nº 283/2005 - Regulamento Técnico para ILPIs',
    'Estatuto do Idoso (Lei 10.741/2003)',
    'Portaria MS/GM nº 2.528/2006 - Política Nacional de Saúde da Pessoa Idosa'
  ]
  
  return {
    grau,
    pontuacao: pontuacaoFinal,
    descricao,
    nivelCuidado,
    criteriosAplicados: criterios,
    fundamentacaoLegal,
    detalhes: {
      funcional: pontosFuncional,
      cognitivo: pontosCognitivo,
      clinico: pontosClinico,
      sensorial: pontosSensorial,
      nutricional: pontosNutricional
    }
  }
}

export function getCorGrau(grau: GrauClassificacao): string {
  switch (grau) {
    case 'I': return 'text-green-600 border-green-500 bg-green-50'
    case 'I-com-apoio': return 'text-emerald-600 border-emerald-500 bg-emerald-50'
    case 'II': return 'text-blue-600 border-blue-500 bg-blue-50'
    case 'III': return 'text-red-600 border-red-500 bg-red-50'
    default: return 'text-gray-600 border-gray-500 bg-gray-50'
  }
}

export function getDescricaoNivelCuidado(nivel: 'básico' | 'intermediário' | 'intensivo'): string {
  switch (nivel) {
    case 'básico': return 'Cuidados básicos de supervisão e apoio pontual'
    case 'intermediário': return 'Cuidados intermediários com assistência parcial regular'
    case 'intensivo': return 'Cuidados intensivos com assistência total e monitoramento constante'
    default: return 'Nível de cuidado não definido'
  }
}
